import type { FinanceAttachmentSummary } from '@nadoutong/contracts';
import { Download, FileText } from 'lucide-react';
import { useState } from 'react';
import {
  downloadTemporaryCreditEvidence,
  useTemporaryCreditEvidence,
} from '../api/credit.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { downloadAttachmentPayload } from '../lib/receipts.ts';
import { Button, Skeleton } from '../ui/index.ts';
import { OpsUserRef } from './identity/OpsUserRef.tsx';
import { formatLocalTimestamp } from './formatters.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';

/**
 * Temporary-credit payment-evidence read-back (router#2000). Same
 * list-then-download shape as `CreditContractDownload` (#1957): the
 * application stores an object key, the download is addressed by id,
 * and the list is what resolves one to the other.
 *
 * This is a READ surface. Finance signs the application and must see
 * the file even when they cannot write on the customer.
 *
 * `evidenceAttachmentKey` narrows the list to the file an application
 * actually cites. A key that matches nothing renders the filtered empty
 * line rather than falling back to the newest upload.
 */

interface TemporaryCreditEvidenceDownloadProps {
  readonly orgId: string;
  readonly evidenceAttachmentKey?: string;
  readonly testId: string;
}

export function TemporaryCreditEvidenceDownload({
  orgId,
  evidenceAttachmentKey,
  testId,
}: TemporaryCreditEvidenceDownloadProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const query = useTemporaryCreditEvidence(orgId);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const all = query.data?.attachments ?? [];
  const rows =
    evidenceAttachmentKey === undefined
      ? all
      : all.filter((attachment) => attachment.object_key === evidenceAttachmentKey);

  async function handleDownload(attachment: FinanceAttachmentSummary): Promise<void> {
    setDownloadingId(attachment.id);
    try {
      const payload = await downloadTemporaryCreditEvidence(orgId, attachment.id, fetchImpl);
      downloadAttachmentPayload(payload);
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <h3 className="m-0 inline-flex items-center gap-1.5 text-section text-paper">
        <FileText aria-hidden="true" className="h-3.5 w-3.5" />
        {t('credit.temporary.evidence.title')}
      </h3>
      {query.isPending ? <Skeleton className="h-10 w-full" /> : null}
      {query.isError ? (
        <p className="m-0 text-dense text-bad" data-testid={`${testId}-error`}>
          {coerceErrorMessage(query.error)}
        </p>
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid={`${testId}-empty`}>
          {t(
            evidenceAttachmentKey === undefined
              ? 'credit.temporary.evidence.empty'
              : 'credit.temporary.evidence.empty_filtered',
          )}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.map((attachment) => (
            <li
              key={attachment.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-line-soft bg-well/50 px-3 py-2"
              data-testid={`${testId}-item-${attachment.id}`}
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-dense text-paper">{attachment.file_name}</span>
                <span className="font-mono text-micro text-paper-low">
                  <OpsUserRef id={attachment.uploaded_by_ops_user_id} /> ·{' '}
                  {formatLocalTimestamp(attachment.created_at)}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                loading={downloadingId === attachment.id}
                onClick={() => void handleDownload(attachment)}
                data-testid={`${testId}-download-${attachment.id}`}
              >
                <Download aria-hidden="true" className="h-3.5 w-3.5" />
                {t('credit.temporary.evidence.download')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
